/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { IComprovantesExtrato } from 'modules/pagamentos/domain/usecases/ComprovantesExtrato'
import ContainerPage from 'shared/components/ContainerPage'
import CartaoNew from '../CartaoNew'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import { colors } from 'shared/styles/colors'
import TableNew from 'shared/components/TableNew'
import Pagination from 'shared/components/Pagination'
import { useFrotista } from 'modules/user/hooks/useFrotista'
import { IBaixarComprovante } from 'modules/pagamentos/domain/usecases/BaixarComprovante'
import { saveFileFromBlob } from 'shared/utils/saveFileFromBlob'
import ComprovantePagamento from '../../PagamentoBoleto/components/ComprovantePagamento'
import { usePagamentoBoleto } from 'modules/pagamentos/hooks/usePagamentoBoleto'
import { useComprovante } from 'modules/pagamentos/hooks/useComprovante'
import { useReactToPrint } from 'react-to-print'
import ETipoComprovanteExtrato from 'modules/pagamentos/domain/entities/ETipoComprovanteExtrato'
import ComprovanteRecargaCelular from './components/RecargaCelular'
import SnackBar from 'shared/components/SnackBar'
import ComprovanteTransferenciaP2P from './components/TransferenciaP2P'
import ComprovanteTransferenciaTED from './components/TransferenciaTED'
import {
  ETipoTransacaoPagamento,
  IComprovanteExtrato,
} from '../../../domain/entities/IComprovantesExtratoResponse'
import IComprovantesExtratoRequest from '../../../domain/entities/IComprovantesExtratoRequest'
import { useHistory } from 'react-router-dom'
import PagamentoRoutes from 'modules/pagamentos/utils/PagamentoRoutes'
import ComprovanteDeposito from './components/ComprovanteDeposito'
import { useFiltro } from '../../../hooks/useFiltro'

interface ComprovantesExtratoProp {
  comprovantesExtrato: IComprovantesExtrato
  baixarComprovante: IBaixarComprovante
}

const ComprovantesExtrato: FunctionComponent<ComprovantesExtratoProp> = ({
  comprovantesExtrato,
  baixarComprovante,
}) => {
  const { frotistaId } = useFrotista()
  const { dateIni, dateFim } = useFiltro()
  const history = useHistory()
  const { setBoleto, setBoletoDeposito } = usePagamentoBoleto()
  const { setTransferenciaP2P, setTransferenciaTED, setRecargaCelular } =
    useComprovante()
  const comprovantePagamentoBoletoRef = useRef()
  const comprovanteDepositoBoletoRef = useRef()
  const comprovanteTransferenciaTEDRef = useRef()
  const comprovanteTransferenciaP2PRef = useRef()
  const comprovanteRecargaCeularRef = useRef()

  const [comprovantes, setComprovantes] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [openErrorMessage, setOpenErrorMessage] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [recordsPerPage, setRecordsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)
  const [protocolo, setProtocolo] = useState('')

  const handleComprovantesExtrato = useCallback(
    (query: IComprovantesExtratoRequest) => {
      return comprovantesExtrato.execute(query)
    },
    [comprovantesExtrato],
  )

  const handleImpressaoComprovantePagamento = useReactToPrint({
    content: () => comprovantePagamentoBoletoRef.current,
  })

  const handleImpressaoComprovanteDeposito = useReactToPrint({
    content: () => comprovanteDepositoBoletoRef.current,
  })

  const handleImpressaoTransferenciaTED = useReactToPrint({
    content: () => comprovanteTransferenciaTEDRef.current,
  })

  const handleImpressaoTransferenciaP2P = useReactToPrint({
    content: () => comprovanteTransferenciaP2PRef.current,
  })

  const handleImpressaoRecargaCelular = useReactToPrint({
    content: () => comprovanteRecargaCeularRef.current,
  })

  const handleTipoDocumento = async (comprovante, tipoArquivo) => {
    if (comprovante[ETipoComprovanteExtrato.EXTRACAO_DADO]) {
      const response = await baixarComprovante.execute({
        frotista_id: frotistaId,
        id_documento:
          tipoArquivo === 'Em CSV'
            ? comprovante.extracaoDado?.idDocumento
            : comprovante.extracaoDado?.idDocumentoPDF,
      })

      saveFileFromBlob(
        response.data,
        tipoArquivo === 'Em CSV'
          ? `Extrato - ${new Date().toLocaleString('pt-BR')}.xlsx`
          : `Extrato - ${new Date().toLocaleString('pt-BR')}.pdf`,
        response.headers['content-type'],
      )
    }

    if (comprovante[ETipoComprovanteExtrato.TRANSACAO_PAGAMENTO]) {
      setProtocolo(comprovante.transacaoPagamento.codigoTransacao)
      const boleto = {
        codigo_barras: comprovante.transacaoPagamento.codigoBarras,
        data_vencimento: new Date(
          comprovante.transacaoPagamento.dataVencimento,
        ),
        tipo: comprovante.transacaoPagamento.tipo,
        valor: comprovante.transacaoPagamento.valor,
        favorecido: comprovante.transacaoPagamento.favorecido,
        protocolo: comprovante.transacaoPagamento.codigoTransacao,
        valor_desconto: comprovante.transacaoPagamento.valorDesconto,
        valor_juros: comprovante.transacaoPagamento.valorJuros,
        valor_multa: comprovante.transacaoPagamento.valorMulta,
        valor_original: comprovante.transacaoPagamento.valorOriginal,
        valor_total: comprovante.transacaoPagamento.valor,
      }
      if (
        comprovante[ETipoComprovanteExtrato.TRANSACAO_PAGAMENTO].tipo ===
        ETipoTransacaoPagamento.BOLETO_PAGAMENTO.toString()
      ) {
        setBoleto(boleto)
        handleImpressaoComprovantePagamento()
      } else {
        setBoletoDeposito(boleto)
        handleImpressaoComprovanteDeposito()
      }
    }

    if (comprovante[ETipoComprovanteExtrato.TRANSFERENCIA_P2P]) {
      setTransferenciaP2P(comprovante.transferenciaP2P)
      handleImpressaoTransferenciaP2P()
    }

    if (comprovante[ETipoComprovanteExtrato.TRANSFERENCIA_TED]) {
      setTransferenciaTED(comprovante.transferenciaTED)
      handleImpressaoTransferenciaTED()
    }

    if (comprovante[ETipoComprovanteExtrato.RECARGA_CELULAR]) {
      setRecargaCelular(comprovante.recargaCelular)
      handleImpressaoRecargaCelular()
    }
  }

  const handleDownload = async (
    comprovante: IComprovanteExtrato,
    tipoArquivo: string,
  ) => {
    setLoading(true)
    setErrorMessage('')
    setOpenErrorMessage(false)
    try {
      await handleTipoDocumento(comprovante, tipoArquivo)
      // todo analisar porque react to print nao esta entendendo que os componentes a serem impressos sao forwarded no primeiro click
      await handleTipoDocumento(comprovante, tipoArquivo)
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message)
      } else {
        setErrorMessage('Erro ao baixar comprovante')
      }
      setOpenErrorMessage(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchComprovantesExtrato = async (page: number) => {
    setLoading(true)
    setErrorMessage('')
    setOpenErrorMessage(false)

    try {
      const response = await handleComprovantesExtrato({
        page,
        per_page: recordsPerPage ?? 10,
        frotista_id: frotistaId ? Number(frotistaId) : undefined,
        data_inicio: dateIni?.toISOString(),
        data_fim: dateFim?.toISOString(),
      })
      setComprovantes(
        response.data.map(comprovante => ({
          ...comprovante,
          handleDownload: async tipoArquivo =>
            await handleDownload(comprovante, tipoArquivo),
        })),
      )
      setTotalRecords(response.total)
      setRecordsPerPage(response.per_page)
      setLoading(false)
    } catch (err: unknown) {
      if (err instanceof Error) {
        setErrorMessage(err.message)
      } else {
        setErrorMessage('Erro buscar comprovantes')
      }
      setLoading(false)
      setOpenErrorMessage(true)
    }
  }

  useEffect(() => {
    fetchComprovantesExtrato(currentPage)
  }, [currentPage, frotistaId])

  return (
    <>
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}

      <ContainerPage
        breadCrumb="Pagamentos/Extrato/Comprovantes"
        showScaffold={true}
        showFooterScaffold={false}
        header={
          <CartaoNew onGoBack={() => history.push(PagamentoRoutes.EXTRATO)} />
        }
        padding="16px 32px"
      >
        {comprovantes && (
          <TableNew data={comprovantes} type="comprovantesExtrato" />
        )}
        <Pagination
          currentPage={currentPage}
          totalRecords={totalRecords}
          recordsPerPage={recordsPerPage}
          setCurrentPage={setCurrentPage}
        />
        <ComprovantePagamento
          ref={comprovantePagamentoBoletoRef}
          protocolo={protocolo}
        />
        <ComprovanteDeposito
          ref={comprovanteDepositoBoletoRef}
          protocolo={protocolo}
        />
        <ComprovanteTransferenciaTED ref={comprovanteTransferenciaTEDRef} />
        <ComprovanteTransferenciaP2P ref={comprovanteTransferenciaP2PRef} />
        <ComprovanteRecargaCelular ref={comprovanteRecargaCeularRef} />
      </ContainerPage>
      <SnackBar
        open={openErrorMessage}
        setOpen={setOpenErrorMessage}
        severity="error"
        message={errorMessage}
        horizontal="right"
      />
    </>
  )
}
export default ComprovantesExtrato
